// Light, non-blocking "next step" banner for the request work surfaces
// (#1595). Pure + presentational: it derives copy from the current
// `status` + caller-supplied predicates and renders ONE neutral status
// line with at most ONE primary next-action button. It never fetches,
// never blocks tab navigation, and never recomputes readiness or
// permissions — the caller threads in the already-gated
// `allowedActions` / `useDraftCompleteness` results.
//
// @aiPurpose Surface the single status-driven "what to do next" hint on
//   the draft editor and the request drawer without adding a transition,
//   field, or permission. The action is offered ONLY when the caller's
//   predicate says the actor is permitted it (admin/`ALL` included).
// @aiInvariant The button is advisory — it invokes an EXISTING handler
//   (the draft Send flow, or the carriers-tab navigation). It must never
//   gate or suppress tab access; the surrounding surface stays freely
//   navigable.

import { useTranslation } from 'react-i18next';
import { RequestStatus } from '@magimp/shared';
import { Button, Icon, type IconName } from '../../../ui/index.js';

/**
 * Draft-surface hint inputs (DRAFT / RETURN). `missingCount` is the
 * length of {@link useDraftCompleteness}'s error list (0 = ready). The
 * Send button shows only when `canSend` (ready AND `send` permitted);
 * an incomplete draft shows a neutral "N fields left" line with no
 * action so Send is never offered while incomplete.
 */
export interface DraftNextStepInput {
  surface: 'draft';
  status: RequestStatus.DRAFT | RequestStatus.RETURN;
  missingCount: number;
  canSend: boolean;
  onSend: () => void;
}

/**
 * Request-surface hint inputs (READY_FOR_WORK / IN_WORK / PROCESSED /
 * terminal). `canPickWinner` is the caller's `canPickWinner(...)` result
 * (admin/`ALL` bypass included). The pick-winner action navigates to the
 * carriers tab via `onPickWinner`; it shows only when there is at least
 * one answered carrier AND the actor is permitted. `hasWinner` is true
 * when `r.winnerBidId != null`; a request with a winner already selected
 * must never show "awaiting selection" copy.
 */
export interface RequestNextStepInput {
  surface: 'request';
  status: RequestStatus;
  hasAnsweredCarrier: boolean;
  hasWinner: boolean;
  canPickWinner: boolean;
  onPickWinner: () => void;
}

export type RequestNextStepHintProps = DraftNextStepInput | RequestNextStepInput;

// Literal i18n key unions so the typed `t()` accepts `model.textKey`
//   without an `as never` cast (the requests namespace is fully typed
//   off `ru.ts`).
type HintTextKey =
  | 'nextStep.draft.incomplete'
  | 'nextStep.draft.ready'
  | 'nextStep.draftReadyPending'
  | 'nextStep.return.incomplete'
  | 'nextStep.return.ready'
  | 'nextStep.returnReadyPending'
  | 'nextStep.readyForWork'
  | 'nextStep.waitingAnswers'
  | 'nextStep.pickWinner'
  | 'nextStep.pickWinnerPending'
  | 'nextStep.winnerSelected'
  | 'nextStep.postponed'
  | 'nextStep.success'
  | 'nextStep.terminal';
type HintActionKey = 'nextStep.action.send' | 'nextStep.action.pickWinner';

interface HintModel {
  /** i18n key for the status/explanation line. */
  textKey: HintTextKey;
  /** Interpolation values for `textKey`, when any. */
  textValues?: Record<string, number>;
  tone: 'info' | 'warn' | 'success' | 'neutral';
  icon: IconName;
  /** Optional single primary action. Absent ⇒ neutral line only. */
  action?: { labelKey: HintActionKey; icon: IconName; onClick: () => void };
}

const TONE_TOKENS: Record<HintModel['tone'], { bg: string; fg: string }> = {
  info: { bg: 'var(--status-info-bg)', fg: 'var(--status-info-fg)' },
  warn: { bg: 'var(--status-warn-bg)', fg: 'var(--status-warn-fg)' },
  success: { bg: 'var(--status-success-bg)', fg: 'var(--status-success-fg)' },
  neutral: { bg: 'var(--status-neutral-bg)', fg: 'var(--status-neutral-fg)' },
};

function draftModel(input: DraftNextStepInput): HintModel {
  // DRAFT → "fill the missing fields and send"; RETURN → "fix and
  //   resend". Both route through the same draft Send flow + readiness
  //   gate. An incomplete draft surfaces WHAT IS MISSING (count) with no
  //   action; Send is never offered while incomplete.
  const isReturn = input.status === RequestStatus.RETURN;
  if (input.missingCount > 0) {
    return {
      textKey: isReturn ? 'nextStep.return.incomplete' : 'nextStep.draft.incomplete',
      textValues: { count: input.missingCount },
      tone: 'warn',
      icon: 'circle-dot',
    };
  }
  if (!input.canSend) {
    // WHY (#1595 AC-2 FR-2): a complete draft whose actor lacks
    //   `requests.transition.send` must not see an instruction to send.
    //   Show a neutral completeness line — the exact analog of
    //   `pickWinnerPending` on the request surface.
    return {
      textKey: isReturn ? 'nextStep.returnReadyPending' : 'nextStep.draftReadyPending',
      tone: 'neutral',
      icon: 'circle-check',
    };
  }
  return {
    textKey: isReturn ? 'nextStep.return.ready' : 'nextStep.draft.ready',
    tone: 'info',
    icon: 'send-horizontal',
    action: {
      labelKey: 'nextStep.action.send',
      icon: 'send-horizontal',
      onClick: input.onSend,
    },
  };
}

function requestModel(input: RequestNextStepInput): HintModel | null {
  switch (input.status) {
    case RequestStatus.READY_FOR_WORK:
      return { textKey: 'nextStep.readyForWork', tone: 'warn', icon: 'clock' };
    case RequestStatus.IN_WORK:
    case RequestStatus.PROCESSED: {
      if (!input.hasAnsweredCarrier) {
        return { textKey: 'nextStep.waitingAnswers', tone: 'info', icon: 'clock' };
      }
      // WHY (#1595 F-005): a request with a winner already selected must
      //   never show "awaiting selection" copy. Check hasWinner first so
      //   this state takes priority over canPickWinner.
      if (input.hasWinner) {
        return { textKey: 'nextStep.winnerSelected', tone: 'neutral', icon: 'check-circle' };
      }
      if (input.canPickWinner) {
        return {
          textKey: 'nextStep.pickWinner',
          tone: 'info',
          icon: 'crown',
          action: {
            labelKey: 'nextStep.action.pickWinner',
            icon: 'crown',
            onClick: input.onPickWinner,
          },
        };
      }
      // WHY (#1595 AC-2 FR-2): a viewer who cannot pick a winner must not
      //   see an action instruction. Show a neutral status line instead.
      return { textKey: 'nextStep.pickWinnerPending', tone: 'neutral', icon: 'clock' };
    }
    // WHY (#1835 BR-4): POSTPONED is active/non-terminal — show a neutral
    //   "on hold" line with NO action affordance (the postpone transition and
    //   any revive CTAs are wired by the China/Russia issues, not here).
    case RequestStatus.POSTPONED:
      return { textKey: 'nextStep.postponed', tone: 'warn', icon: 'clock' };
    case RequestStatus.SUCCESS:
      return { textKey: 'nextStep.success', tone: 'success', icon: 'check-circle' };
    case RequestStatus.CLOSED:
    case RequestStatus.REFUSED:
    case RequestStatus.CANNOT_TRANSPORT:
    case RequestStatus.ARCHIVE:
      return { textKey: 'nextStep.terminal', tone: 'neutral', icon: 'flag' };
    // DRAFT / RETURN are owned by the draft surface; the request drawer
    //   never mounts the hint for them, so render nothing defensively.
    default:
      return null;
  }
}

export function RequestNextStepHint(props: RequestNextStepHintProps) {
  const { t } = useTranslation('requests');
  const model = props.surface === 'draft' ? draftModel(props) : requestModel(props);
  if (model === null) return null;
  const tokens = TONE_TOKENS[model.tone];
  // WHY (#1595): the request drawer mounts the hint as a flex child
  //   between the header and the tabs, so it carries the same
  //   `12px 24px 0` inset the return-banner uses. The draft surface
  //   mounts it inside the already-padded editor body, so it takes no
  //   outer margin there.
  const margin = props.surface === 'request' ? '12px 24px 0' : 0;
  return (
    <div
      role="note"
      data-testid="request-next-step-hint"
      style={{
        margin,
        padding: '10px 14px',
        background: tokens.bg,
        borderRadius: 'var(--r-2)',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flex: 'none',
        flexWrap: 'wrap',
      }}
    >
      <Icon name={model.icon} size={16} style={{ color: tokens.fg, flex: 'none' }} />
      <span style={{ fontSize: 13, color: 'var(--fg-1)', flex: 1, minWidth: 0 }}>
        {model.textValues ? t(model.textKey, model.textValues) : t(model.textKey)}
      </span>
      {model.action && (
        <Button kind="primary" size="sm" icon={model.action.icon} onClick={model.action.onClick}>
          {t(model.action.labelKey)}
        </Button>
      )}
    </div>
  );
}
